// Atribusi iklan pengunjung TOKO (mig 4170) — helper MURNI, diuji
// storeAttribution.test.ts. Aturan BENTUK (lowercase, buang www.,
// self-referrer→null) dipakai ulang dari platformVisitor.ts — SATU salinan;
// SQL `record_page_view` v5 hanya menjaga panjang & kekosongan.
//
// Dua pemakaian:
//  1. VisitorTracker: kirim utm/referrer kunjungan ke `record_page_view`
//     (first-touch per hari diselesaikan server, isi-kalau-kosong).
//  2. FIRST-TOUCH untuk ATRIBUSI ORDER: disimpan localStorage (per-origin —
//     tiap subdomain penyewa punya storage sendiri, otomatis ter-scope toko)
//     lalu dikirim `record_order_attribution` saat checkout sukses.

import {
  hasAttribution,
  parseReferrerHost,
  readUtm,
} from "@/lib/platformVisitor";

/** localStorage: first-touch atribusi utk order (berdampingan galea-visitor-id). */
export const STORE_FIRST_TOUCH_KEY = "galea-utm-first";

/**
 * Jendela atribusi first-touch: 30 hari — standar pelaporan iklan. Lebih tua
 * dari itu, klik baru MENGGANTIKAN yang lama (bukan first-touch selamanya:
 * pembeli langganan akan selamanya tercatat "dari iklan Maret" padahal
 * kampanyenya sudah lama mati).
 */
export const FIRST_TOUCH_TTL_DAYS = 30;

export type StoreAttribution = {
  referrerHost: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
};

export type StoreFirstTouch = StoreAttribution & {
  /** ISO saat first-touch terekam — dasar kedaluwarsa 30 hari. */
  at: string;
};

/** Bahan atribusi satu kunjungan storefront. */
export function buildStoreAttribution(input: {
  search?: string | null;
  referrer?: string | null;
  currentHost?: string | null;
}): StoreAttribution {
  const utm = readUtm(input.search);
  return {
    referrerHost: parseReferrerHost(input.referrer, input.currentHost),
    utmSource: utm.utmSource,
    utmMedium: utm.utmMedium,
    utmCampaign: utm.utmCampaign,
  };
}

/** True bila kunjungan membawa UTM apa pun (bukan sekadar referrer). */
export function hasUtmAttribution(a: StoreAttribution): boolean {
  return hasAttribution({
    path: "/",
    referrerHost: a.referrerHost,
    utmSource: a.utmSource,
    utmMedium: a.utmMedium,
    utmCampaign: a.utmCampaign,
  });
}

/** Ada sesuatu yang layak disimpan sebagai first-touch? (UTM ATAU perujuk
 *  eksternal — kunjungan dari bio IG tanpa UTM tetap atribusi yang berharga.) */
export function hasAnyAttribution(a: StoreAttribution): boolean {
  return hasUtmAttribution(a) || a.referrerHost !== null;
}

/** Parse isi localStorage → StoreFirstTouch, atau null bila rusak/asing. */
export function parseFirstTouch(raw: string | null): StoreFirstTouch | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!v || typeof v !== "object" || typeof v.at !== "string") return null;
    const s = (x: unknown): string | null =>
      typeof x === "string" && x.trim() !== "" ? x : null;
    const parsed: StoreFirstTouch = {
      referrerHost: s(v.referrerHost),
      utmSource: s(v.utmSource),
      utmMedium: s(v.utmMedium),
      utmCampaign: s(v.utmCampaign),
      at: v.at,
    };
    if (!Number.isFinite(Date.parse(parsed.at))) return null;
    if (!hasAnyAttribution(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * First-touch tersimpan masih berlaku sebagai pemenang? False = kedaluwarsa
 * (>30 hari) atau tak ada — kunjungan ber-atribusi berikutnya menggantikan.
 */
export function firstTouchStillValid(
  existing: StoreFirstTouch | null,
  nowMs: number
): boolean {
  if (!existing) return false;
  const atMs = Date.parse(existing.at);
  if (!Number.isFinite(atMs)) return false;
  return nowMs - atMs < FIRST_TOUCH_TTL_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * GERBANG KIRIM kunjungan toko — cermin `shouldRecordPlatformVisit` (mig 4100):
 * kirim bila hari ini belum tercatat ATAU URL membawa UTM. Klik iklan SELALU
 * dilaporkan walau hari sudah tercatat — server mengisi kolom atribusi yang
 * masih kosong; tanpa aturan ini klik iklan sore setelah kunjungan organik
 * pagi tak pernah sampai server.
 */
export function shouldRecordStoreVisit(input: {
  today: string;
  lastVisitedDay: string | null;
  hasUtm: boolean;
}): boolean {
  if (input.hasUtm) return true;
  return input.lastVisitedDay !== input.today;
}
